import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {FlatpickrOptions} from 'ng2-flatpickr';
import {French} from 'flatpickr/dist/l10n/fr.js';
import {forkJoin, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {DonneesHistoriqueRow} from '../parametre-donnees.types';
import {ParametreDonneesApiService} from '../parametre-donnees-api.service';
import {resolveRefModuleTableDbId} from '../parametre-donnees-api.models';
import {ParametreDonneesStateService} from '../parametre-donnees-state.service';
import {getParametrageSessionIds} from '../parametre-donnees-session.util';
import {DonneesDataTableColumn} from '../parametre-donnees-data-table/parametre-donnees-data-table.component';

@Component({
  selector: 'app-parametre-donnees-historique-tab',
  templateUrl: './parametre-donnees-historique-tab.component.html',
  styleUrls: ['./parametre-donnees-historique-tab.component.scss']
})
export class ParametreDonneesHistoriqueTabComponent implements OnChanges, OnInit {
  @Input() moduleKey = 'produits';

  all: DonneesHistoriqueRow[] = [];
  filtered: DonneesHistoriqueRow[] = [];

  private connectedUserId = '';
  private connectedUserLabel = '';

  private fieldIdToLibelle = new Map<string, string>();

  filterDateFrom = '';
  filterDateTo = '';

  readonly histoDateFlatpickrOptions: FlatpickrOptions = {
    locale: French,
    altInput: true,
    altFormat: 'd/m/Y',
    dateFormat: 'Y-m-d',
    allowInput: true,
    disableMobile: true,
    monthSelectorType: 'dropdown',
    altInputClass: 'form-control form-control-sm pd-histo-fp-input',
    onReady: (_selectedDates, _dateStr, instance) => {
      instance.calendarContainer.classList.add('pd-histo-fp-cal');
    }
  };
  filterUser = '';
  filterAction = '';
  filterObjet = '';

  readonly histoColumns: DonneesDataTableColumn[] = [
    { key: 'id', label: 'ID', width: '90px' },
    { key: 'date', label: 'Date', width: '160px' },
    { key: 'utilisateur', label: 'Utilisateur' },
    { key: 'action', label: 'Action' },
    { key: 'objet', label: 'Objet' },
    { key: 'ancienneValeur', label: 'Ancienne valeur' },
    { key: 'nouvelleValeur', label: 'Nouvelle valeur' },
    { key: 'source', label: 'Source' }
  ];

  /** Clés métier affichées pour un snapshot de champ (table param). */
  private readonly paramFieldSummaryKeys: string[] = [
    'id',
    'table_db_id',
    'table_db',
    'champ_db',
    'libelle_champ',
    'type',
    'ordre',
    'groupe',
    'description',
    'placeholder',
    'valeur_defaut',
    'obligatoire',
    'actif',
    'unique_value',
    'lecture_seule',
    'utilise_dans'
  ];

  /** Blocs JSON ou colonnes techniques masqués dans l’historique (résumé lisible). */
  private readonly historiqueJsonNoiseKeys = new Set([
    'display_json',
    'api_json',
    'usage_json',
    'station_id',
    'user_id'
  ]);

  currentPage = 1;
  pageSize = 10;

  constructor(
    private readonly state: ParametreDonneesStateService,
    private readonly api: ParametreDonneesApiService,
    private readonly toastr: ToastrService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.refreshConnectedUserFromStorage();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['moduleKey']) {
      this.fieldIdToLibelle.clear();
      this.all = this.state.getHistorique(this.moduleKey);
      this.applyFilters();
      this.refreshParamFieldLibelleCache();
    }
  }

  refreshFromServer(): void {
    this.state
      .loadHistoriqueApi(this.moduleKey, {
        page: 1,
        page_size: 500,
        date_from: this.filterDateFrom || undefined,
        date_to: this.filterDateTo || undefined,
        action: this.filterAction.trim() || undefined,
        objet: this.filterObjet.trim() || undefined
      })
      .subscribe({
        next: () => {
          this.fieldIdToLibelle.clear();
          this.all = this.state.getHistorique(this.moduleKey);
          this.applyFilters();
          this.refreshParamFieldLibelleCache();
          this.toastr.success('Historique actualisé.', 'Succès');
        },
        error: () => {}
      });
  }

  applyFilters(): void {
    let list = [...this.all];
    if (this.filterUser.trim()) {
      const q = this.filterUser.toLowerCase();
      list = list.filter((r) => {
        const id = r.utilisateur.toLowerCase();
        const label = this.displayUtilisateurColumn(r.utilisateur).toLowerCase();
        return id.includes(q) || label.includes(q);
      });
    }
    if (this.filterAction.trim()) {
      const q = this.filterAction.toLowerCase();
      list = list.filter((r) => r.action.toLowerCase().includes(q));
    }
    if (this.filterObjet.trim()) {
      const q = this.filterObjet.toLowerCase();
      list = list.filter((r) => {
        const raw = r.objet.toLowerCase();
        const pid = this.parseObjetAsParamFieldId(r.objet);
        const lib =
          pid != null ? (this.fieldIdToLibelle.get(pid) ?? '').toLowerCase() : '';
        return raw.includes(q) || lib.includes(q);
      });
    }
    if (this.filterDateFrom) {
      const t = new Date(this.filterDateFrom).getTime();
      list = list.filter((r) => new Date(r.date).getTime() >= t);
    }
    if (this.filterDateTo) {
      const t = new Date(this.filterDateTo).getTime() + 86400000;
      list = list.filter((r) => new Date(r.date).getTime() < t);
    }
    this.filtered = list;
    this.currentPage = 1;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
  }

  get pageSlice(): DonneesHistoriqueRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filtered.slice(start, start + this.pageSize);
  }

  get pageFrom(): number {
    if (this.filtered.length === 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageTo(): number {
    return Math.min(this.filtered.length, this.currentPage * this.pageSize);
  }

  onPageChange(p: number): void {
    this.currentPage = p;
  }

  onPageSizeChange(s: number): void {
    this.pageSize = s;
    this.currentPage = 1;
  }

  tableRows(): Record<string, unknown>[] {
    return this.pageSlice.map((r) => ({
      id: r.id,
      date: new Date(r.date).toLocaleString(),
      utilisateur: this.displayUtilisateurColumn(r.utilisateur),
      action: r.action,
      objet: this.displayObjetColumn(r.objet),
      ancienneValeur: this.stripDisplaySymbols(r.ancienneValeur),
      nouvelleValeur: this.stripDisplaySymbols(r.nouvelleValeur),
      source: r.source
    }));
  }

  /** Nettoyage purement visuel : enlève { } [ ] et \ de l'affichage. */
  private stripDisplaySymbols(v: unknown): string {
    const s = v === null || v === undefined ? '' : String(v);
    const trimmed = s.trim();
    const jsonSummary = this.tryHistoriqueCompactJson(trimmed);
    if (jsonSummary !== null) {
      return jsonSummary;
    }
    // supprimer accolades, crochets et antislash (on garde les virgules)
    let cleaned = s.replace(/[{}\[\]\\]/g, '');
    // ne pas afficher certains "mots clés" techniques s'ils existent dans la chaîne
    cleaned = cleaned
      .replace(/"?(options_json)"?\s*:?\s*/gi, '')
      .replace(/"?(liste)"?\s*:?\s*/gi, '')
      .replace(/"?(monoOuMultiple)"?\s*:?\s*/gi, '')
      .replace(/"?(valeurs)"?\s*:?\s*/gi, '');
    // si on trouve des guillemets successifs, réduire à un seul
    cleaned = cleaned.replace(/"{2,}/g, '"');
    // virgules doubles/orphelines après retrait des {} et des clés (hors texte entre guillemets)
    cleaned = this.collapseDuplicateCommasOutsideQuotes(cleaned);
    // si on trouve "label", forcer un retour à la ligne avant (sauf si déjà au début)
    return cleaned.replace(/\blabel\b/gi, (m, offset) => (offset === 0 ? m : `\n${m}`));
  }

  /** Réduit ",," en "," uniquement dans les segments hors guillemets — préserve les virgules dans les valeurs citées. */
  private collapseDuplicateCommasOutsideQuotes(text: string): string {
    const parts = text.split(/("[^"]*")/g);
    return parts
      .map((part, i) =>
        i % 2 === 1 ? part : part.replace(/,\s*,+/g, ', ')
      )
      .join('');
  }

  /**
   * Si la valeur est un objet JSON, masque les blocs techniques (display/api/usage)
   * et résume un champ param ; sinon retourne null pour garder le traitement texte.
   */
  private tryHistoriqueCompactJson(trimmed: string): string | null {
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
      return null;
    }
    let parsed: Record<string, unknown> | null = null;
    try {
      let raw: unknown = JSON.parse(trimmed);
      if (typeof raw === 'string') {
        const inner = raw.trim();
        if (inner.startsWith('{') && inner.endsWith('}')) {
          raw = JSON.parse(inner);
        }
      }
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        parsed = { ...(raw as Record<string, unknown>) };
      }
    } catch {
      return null;
    }
    if (!parsed) {
      return null;
    }

    for (const k of this.historiqueJsonNoiseKeys) {
      delete parsed[k];
    }

    const isParamField =
      Object.prototype.hasOwnProperty.call(parsed, 'champ_db') ||
      Object.prototype.hasOwnProperty.call(parsed, 'libelle_champ');

    if (isParamField) {
      return this.formatParamFieldHistoriqueSummary(parsed);
    }

    const keys = Object.keys(parsed);
    if (keys.length === 0) {
      return '';
    }
    return this.formatHistoriqueGenericObject(parsed);
  }

  /**
   * Objet JSON hors champ param : évite JSON.stringify sur options_json (sinon { } et \\ réapparaissent).
   */
  private formatHistoriqueGenericObject(o: Record<string, unknown>): string {
    const lines: string[] = [];
    for (const key of Object.keys(o)) {
      const val = o[key];
      if (val === null || val === undefined || val === '') {
        continue;
      }
      if (key === 'options_json') {
        const block = this.summarizeOptionsJsonForHistorique(val);
        if (block) {
          lines.push(block);
        }
        continue;
      }
      lines.push(`${key}: ${this.formatHistoriquePrimitive(val)}`);
    }
    return lines.join('\n');
  }

  /** Résumé lignes pour un champ personnalisé (sans display/api/usage). */
  private formatParamFieldHistoriqueSummary(o: Record<string, unknown>): string {
    const lines: string[] = [];
    for (const key of this.paramFieldSummaryKeys) {
      if (!Object.prototype.hasOwnProperty.call(o, key)) {
        continue;
      }
      const val = o[key];
      if (val === null || val === undefined || val === '') {
        continue;
      }
      lines.push(`${key}: ${this.formatHistoriquePrimitive(val)}`);
    }
    const optLine = this.summarizeOptionsJsonForHistorique(o['options_json']);
    if (optLine) {
      lines.push(optLine);
    }
    return lines.join('\n');
  }

  private formatHistoriquePrimitive(val: unknown): string {
    if (val === null || val === undefined) {
      return '';
    }
    if (typeof val === 'boolean') {
      return val ? 'true' : 'false';
    }
    if (typeof val === 'number') {
      return String(val);
    }
    if (typeof val === 'string') {
      return val;
    }
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }

  /**
   * options_json.liste : en-tête + une ligne par option (label, valeur, couleur).
   */
  private formatListeOptionsJsonSummary(liste: Record<string, unknown>): string {
    const mono = liste['monoOuMultiple'];
    const monoStr = mono == null ? 'mono' : String(mono);
    const valeurs = Array.isArray(liste['valeurs']) ? liste['valeurs'] : [];
    const maxLines = 100;
    const lines: string[] = [
      `options_json: liste (${monoStr}, ${valeurs.length} option(s))`
    ];
    const n = Math.min(valeurs.length, maxLines);
    for (let i = 0; i < n; i++) {
      const it = valeurs[i];
      if (!it || typeof it !== 'object') {
        continue;
      }
      const row = this.formatListeOptionLabelValeurCouleur(it as Record<string, unknown>);
      if (row) {
        lines.push(`  ${i + 1}. ${row}`);
      }
    }
    if (valeurs.length > maxLines) {
      lines.push(`  … (+${valeurs.length - maxLines} autre(s))`);
    }
    return lines.join('\n');
  }

  private formatListeOptionLabelValeurCouleur(it: Record<string, unknown>): string {
    const label = it['label'] != null ? String(it['label']).trim() : '';
    const value = it['value'] != null ? String(it['value']).trim() : '';
    const couleur = it['couleur'] != null ? String(it['couleur']).trim() : '';
    const parts: string[] = [];
    if (label) {
      parts.push(`label: ${label}`);
    }
    if (value) {
      parts.push(`valeur: ${value}`);
    }
    if (couleur) {
      parts.push(`couleur: ${couleur}`);
    }
    return parts.join(' | ');
  }

  /** Une ligne lisible pour options_json (liste / relation / nombre). */
  private summarizeOptionsJsonForHistorique(raw: unknown): string {
    if (raw === null || raw === undefined) {
      return '';
    }
    let opt: unknown = raw;
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (!t || (!t.startsWith('{') && !t.startsWith('['))) {
        return '';
      }
      try {
        opt = JSON.parse(t);
      } catch {
        return '';
      }
    }
    if (!opt || typeof opt !== 'object') {
      return '';
    }
    const obj = opt as Record<string, unknown>;
    if (obj['liste'] && typeof obj['liste'] === 'object') {
      return this.formatListeOptionsJsonSummary(obj['liste'] as Record<string, unknown>);
    }
    if (obj['relation'] && typeof obj['relation'] === 'object') {
      return `options_json: relation ${JSON.stringify(obj['relation'])}`;
    }
    if (obj['nombre'] && typeof obj['nombre'] === 'object') {
      return `options_json: nombre ${JSON.stringify(obj['nombre'])}`;
    }
    return 'options_json: (présent)';
  }

  private formatJsonLikeValue(v: unknown): string {
    if (v == null) {
      return '';
    }

    if (typeof v !== 'string') {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }

    const s = v.trim();
    if (!s) {
      return '';
    }

    const renderOptionsJson = (root: unknown): string => {
      if (!root || typeof root !== 'object') return '';
      const o = root as Record<string, unknown>;
      let options = (o['options_json'] ?? o) as unknown;
      // options_json peut arriver comme string JSON encodée
      if (typeof options === 'string') {
        const t = options.trim();
        if (t.startsWith('{') || t.startsWith('[')) {
          try {
            options = JSON.parse(t);
          } catch {
            // ignore
          }
        }
      }
      if (!options || typeof options !== 'object') return '';
      const optObj = options as Record<string, unknown>;
      let liste = (optObj['liste'] ?? optObj) as unknown;
      if (typeof liste === 'string') {
        const t2 = liste.trim();
        if (t2.startsWith('{') || t2.startsWith('[')) {
          try {
            liste = JSON.parse(t2);
          } catch {
            // ignore
          }
        }
      }
      if (!liste || typeof liste !== 'object') return '';
      const listeObj = liste as Record<string, unknown>;

      const mono = listeObj['monoOuMultiple'];
      const monoStr =
        mono == null ? '' : (typeof mono === 'string' ? mono.trim() : String(mono).trim());

      const valeursRaw = listeObj['valeurs'];
      const valeursArr = Array.isArray(valeursRaw) ? valeursRaw : [];
      const pairs: {label: string; value: string}[] = [];
      for (const it of valeursArr) {
        if (!it || typeof it !== 'object') continue;
        const itObj = it as Record<string, unknown>;
        const label = itObj['label'] != null ? String(itObj['label']).trim() : '';
        const value = itObj['value'] != null ? String(itObj['value']).trim() : '';
        if (label || value) {
          pairs.push({label, value});
        }
        if (pairs.length >= 8) break;
      }

      let maxLeft = 0;
      for (const p of pairs) {
        maxLeft = Math.max(maxLeft, `label : ${p.label}`.length);
      }

      const rowHtmls: string[] = [];
      for (const p of pairs) {
        const left = `label : ${p.label}`;
        const paddedLeft = left + ' '.repeat(Math.max(0, maxLeft - left.length));
        rowHtmls.push(
          `${this.escapeHtml(paddedLeft)} <span class="pd-opt-sep">|</span> valeur : ${this.escapeHtml(p.value)}`
        );
      }

      if (!monoStr && rowHtmls.length === 0) return '';
      const linesInner =
        (monoStr ? `${this.escapeHtml(monoStr)}<br />` : '') + rowHtmls.join('<br />');
      return `<div class="pd-opt-lines">${linesInner}</div>`;
    };

    /** api_json : propriétés visibles, sans clé api_json ni accolades. */
    const renderApiJson = (root: unknown): string => {
      if (!root || typeof root !== 'object') return '';
      const o = root as Record<string, unknown>;
      let payload: Record<string, unknown> | null = null;
      if ('api_json' in o && o['api_json'] != null) {
        let inner: unknown = o['api_json'];
        if (typeof inner === 'string') {
          const t = inner.trim();
          if (t.startsWith('{') || t.startsWith('[')) {
            try {
              inner = JSON.parse(t);
            } catch {
              return '';
            }
          }
        }
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          payload = inner as Record<string, unknown>;
        }
      } else if (
        'exposeApi' in o ||
        'nomTechniqueApi' in o ||
        'lectureSeuleApi' in o
      ) {
        payload = o;
      }
      if (!payload) return '';

      const lines: string[] = [];
      for (const [k, val] of Object.entries(payload)) {
        if (lines.length >= 16) break;
        let disp = '';
        if (val === null || val === undefined) {
          disp = '';
        } else if (typeof val === 'boolean') {
          disp = val ? 'true' : 'false';
        } else if (typeof val === 'number') {
          disp = String(val);
        } else if (typeof val === 'string') {
          disp = val;
        } else {
          try {
            disp = JSON.stringify(val);
          } catch {
            disp = String(val);
          }
        }
        lines.push(
          `<div class="pd-api-line"><span class="pd-api-k">${this.escapeHtml(k)}</span> : <span class="pd-api-v">${this.escapeHtml(disp)}</span></div>`
        );
      }
      if (!lines.length) return '';
      return `<div class="pd-opt-lines pd-api-json-lines">${lines.join('')}</div>`;
    };

    // 1) Cas api_json
    if (s.includes('api_json')) {
      const candidates: string[] = [s];
      if (!(s.startsWith('{') && s.endsWith('}'))) {
        candidates.push(`{${s}}`);
      }
      for (const cand of candidates) {
        try {
          const parsedApi = JSON.parse(cand) as unknown;
          const renderedApi = renderApiJson(parsedApi);
          if (renderedApi) return renderedApi;
        } catch {
          /* ignore */
        }
      }
    }

    // 2) Cas spécial options_json même si la chaîne n'est pas un JSON complet
    if (s.includes('"options_json"') || s.includes('options_json')) {
      const candidates: string[] = [];
      // JSON complet
      candidates.push(s);
      // Fragment type:  "options_json": {...}
      if (!(s.startsWith('{') && s.endsWith('}'))) {
        candidates.push(`{${s}}`);
      }
      for (const cand of candidates) {
        try {
          const parsedOpt = JSON.parse(cand) as unknown;
          const rendered = renderOptionsJson(parsedOpt);
          if (rendered) return rendered;
        } catch {
          // ignore
        }
      }
    }

    // 3) Si ce n’est pas du JSON, on renvoie brut.
    const looksJson = (s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'));
    if (!looksJson) {
      return s;
    }

    // Essayer de parser et d’extraire des valeurs "utiles" (sans structure JSON).
    try {
      const parsed = JSON.parse(s) as unknown;

      const out: string[] = [];

      const formatPrimitive = (x: unknown): string => {
        if (x == null) return '';
        if (typeof x === 'string') return x.trim();
        if (typeof x === 'number' || typeof x === 'boolean') return String(x);
        return '';
      };

      const pushPair = (k: string, val: unknown) => {
        if (out.length >= 8) return;
        const fv = formatPrimitive(val);
        if (!fv) return;
        out.push(`"${k}": ${fv}`);
      };

      const formatLabelValueArray = (arr: unknown[]): string => {
        const parts: string[] = [];
        for (const it of arr) {
          if (!it || typeof it !== 'object') continue;
          const o = it as Record<string, unknown>;
          const label = formatPrimitive(o['label']);
          const value = formatPrimitive(o['value']);
          if (label && value) parts.push(`${label} (${value})`);
          else if (label) parts.push(label);
          else if (value) parts.push(value);
          if (parts.length >= 8) break;
        }
        return parts.join(', ');
      };

      const walk = (x: unknown, path: string[] = []) => {
        if (out.length >= 8) return; // éviter les sorties énormes

        if (Array.isArray(x)) {
          // Cas courant: tableau d'objets {label,value}
          const looksLikeLabelValue =
            x.length > 0 &&
            x.every((it) => it && typeof it === 'object' && ('label' in (it as any) || 'value' in (it as any)));
          if (looksLikeLabelValue) {
            const rendered = formatLabelValueArray(x);
            if (rendered) {
              const key = path.length ? path[path.length - 1] : 'valeurs';
              out.push(`"${key}": ${rendered}`);
            }
            return;
          }

          // Sinon: on parcourt quelques éléments seulement
          for (const it of x.slice(0, 3)) {
            walk(it, path);
            if (out.length >= 8) return;
          }
          return;
        }

        if (x && typeof x === 'object') {
          for (const [k, v2] of Object.entries(x as Record<string, unknown>)) {
            if (v2 && typeof v2 === 'object') {
              // tenter d'extraire des primitives du sous-objet
              walk(v2, [...path, k]);
            } else {
              pushPair(k, v2);
            }
            if (out.length >= 8) return;
          }
          return;
        }
      };

      walk(parsed);
      if (out.length) {
        return out.join(', ');
      }
    } catch {
      // fallback ci-dessous
    }

    // Fallback: extraire les segments entre guillemets (jusqu’à la prochaine virgule implicitement).
    const matches: string[] = [];
    const re = /"([^"]+)"/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s)) !== null && matches.length < 50) {
      if (m[1]) {
        matches.push(m[1]);
      }
    }
    if (matches.length) {
      // En JSON, les matches alternent souvent clé/valeur. On préfère les "valeurs" (index impairs).
      const values = matches.filter((_, idx) => idx % 2 === 1);
      return (values.length ? values : matches).slice(0, 8).join(', ');
    }

    return s;
  }

  /** Valeur cellule historique : HTML (options_json) ou texte échappé avec retours ligne. */
  private formatHistoriqueCellHtml(v: unknown): string {
    const raw = this.formatJsonLikeValue(v);
    if (raw.includes('pd-opt-lines')) {
      return raw;
    }
    if (this.isHistoriqueVideVisuel(raw)) {
      return this.escapeHtml('__');
    }
    return this.escapeHtml(raw).replace(/\n/g, '<br />');
  }

  /** Vide / JSON vide : afficher __ au lieu de {} ou []. */
  private isHistoriqueVideVisuel(s: string): boolean {
    const t = s.trim();
    return t === '' || t === '{}' || t === '[]';
  }

  private escapeHtml(t: string): string {
    return t
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private displayUtilisateurColumn(storedUserId: string): string {
    const s = String(storedUserId).trim();
    if (this.connectedUserId && s === this.connectedUserId && this.connectedUserLabel) {
      return this.connectedUserLabel;
    }
    return s;
  }

  private displayObjetColumn(raw: string): string {
    const id = this.parseObjetAsParamFieldId(raw);
    if (id === null) {
      return raw;
    }
    const lib = this.fieldIdToLibelle.get(id);
    return lib != null && lib !== '' ? lib : raw;
  }

  private parseObjetAsParamFieldId(objet: string): string | null {
    const t = String(objet).trim();
    if (!/^\d+$/.test(t)) {
      return null;
    }
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) {
      return null;
    }
    return t;
  }

  private refreshParamFieldLibelleCache(): void {
    this.state.ensureRefModules().subscribe(() => {
      const ref = this.state.getModuleRef(this.moduleKey);
      const { stationId, userId } = getParametrageSessionIds();
      if (!ref || stationId <= 0 || userId <= 0) {
        return;
      }

      this.api
        .getParamTableFields(stationId, userId, resolveRefModuleTableDbId(ref))
        .pipe(
          catchError(() => of([])),
          switchMap((fields) => {
            for (const f of fields) {
              this.fieldIdToLibelle.set(String(f.id), f.libelle_champ);
            }
            const missing: string[] = [];
            for (const r of this.all) {
              const pid = this.parseObjetAsParamFieldId(r.objet);
              if (pid !== null && !this.fieldIdToLibelle.has(pid) && !missing.includes(pid)) {
                missing.push(pid);
              }
            }
            if (missing.length === 0) {
              return of(void 0);
            }
            return forkJoin(
              missing.map((id) =>
                this.api.getParamTableFieldById(Number(id)).pipe(
                  map((dto) => ({ id, lib: dto.libelle_champ })),
                  catchError(() => of({ id, lib: '' }))
                )
              )
            ).pipe(
              tap((pairs) => {
                for (const { id, lib } of pairs) {
                  if (lib) {
                    this.fieldIdToLibelle.set(id, lib);
                  }
                }
              }),
              map(() => void 0)
            );
          })
        )
        .subscribe({
          next: () => {
            this.applyFilters();
            this.cdr.markForCheck();
          },
          error: () => {}
        });
    });
  }

  private refreshConnectedUserFromStorage(): void {
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw == null || raw === '') {
        this.connectedUserId = '';
        this.connectedUserLabel = '';
        return;
      }
      const u = JSON.parse(raw) as Record<string, unknown>;
      const idRaw = u['id'] ?? u['user_id'];
      this.connectedUserId = idRaw != null ? String(idRaw).trim() : '';
      const prenom = String(u['prenom'] ?? u['firstName'] ?? u['first_name'] ?? '').trim();
      const nom = String(u['nom'] ?? u['lastName'] ?? u['last_name'] ?? '').trim();
      let label = [prenom, nom].filter(Boolean).join(' ');
      if (!label && u['nom_prenom']) {
        label = String(u['nom_prenom']).trim();
      }
      this.connectedUserLabel = label;
    } catch {
      this.connectedUserId = '';
      this.connectedUserLabel = '';
    }
  }
}
